import { useState } from "react";
import { useNavigate } from "react-router-dom";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* USER DEFINED COMPONENTS */
import Input from "../../components/input";
import LoadingButton from "../../components/button";

/* ICONS */
import { IoMdEye, IoMdEyeOff } from "react-icons/io";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* REDUX IMPORTS */
import { useDispatch } from "react-redux";
import actions from "../../redux/actions/userAction";

import { useFormik } from "formik";
import * as yup from "yup";

/* ASSETS */
import Logo from "../../assets/images/logo-dark.png";

/* API IMPORT */
import { adminRegister } from "../../service/api";

/* VALIDATION SCHEMA */
const registerValidation = yup.object().shape({
  firstName: yup
    .string()
    .min(2, "Too short")
    .required("First name is required"),
  lastName: yup
    .string()
    .min(2, "Too short")
    .required("Last name is required"),
  email: yup
    .string()
    .email()
    .required("Email address is required"),

  password: yup
    .string()
    .min(2, "Too short")
    .required("Password is required"),
});

const AuthLogin = () => {
  // GLOBAL STATES
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // STATE VARIABLES
  const [loader, setLoader] = useState(false);
  const [registerPassword, setRegisterPassword] = useState(false);

  // FORMIK REGISTRATION FUNCTION
  const registerHandler = useFormik({
    initialValues: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
    },
    validationSchema: registerValidation,
    onSubmit: (values) => {
      setLoader(true);
      adminRegister(values)
        .then((res) => {
          if (res?.status) {
            dispatch(actions.setUser(res?.data?.userData));
            dispatch(actions.setToken(res?.data?.token));
            dispatch(actions.setLoggedIn(true));
            toast.success(res?.message);
            registerHandler.resetForm();
            navigate("/");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message);
        })
        .finally(() => {
          setLoader(false);
        });
    },
  });

  return (
    <section className="bg-authBg">
      {/* AUTH FORM */}
      <div className="h-authForm">
        <div className="container h-full flex items-center justify-center">
          <div className="w-[60vh] bg-white rounded-lg border border-authBorder p-10">
            <img src={Logo} className="w-32 h-auto" alt="Farmers Insurance" />
            <div className="mt-5">
              <h1 className="text-2xl font-ibm-bold text-black">
                Admin Portal Registration
              </h1>
              <h2 className="text-lg font-ibm-regular text-greyText mt-1">
                Access your e-commerce dashboard and manage your orders and
                online store via this dashboard
              </h2>
            </div>

            <form className="mt-5" onSubmit={registerHandler.handleSubmit}>
              <div className="flex gap-3">
                {/* FIRST NAME */}
                <div className="form-group w-full">
                  <label className="text-sm font-ibm-semibold text-black">
                    First Name <span className="text-red-500">*</span>
                    <div className="mt-1">
                      <Input
                        type="text"
                        name="firstName"
                        id="firstName"
                        placeholder="First Name"
                        value={registerHandler.values.firstName}
                        onChange={registerHandler.handleChange}
                        errorHandler={
                          registerHandler.errors.firstName &&
                          registerHandler.touched.firstName
                        }
                      />
                    </div>
                  </label>
                </div>
                {/* LAST NAME */}
                <div className="form-group w-full">
                  <label className="text-sm font-ibm-semibold text-black">
                    Last Name <span className="text-red-500">*</span>
                    <div className="mt-1 relative">
                      <Input
                        type="text"
                        name="lastName"
                        id="lastName"
                        placeholder="Last Name"
                        value={registerHandler.values.lastName}
                        onChange={registerHandler.handleChange}
                        errorHandler={
                          registerHandler.errors.lastName &&
                          registerHandler.touched.lastName
                        }
                      />
                    </div>
                  </label>
                </div>
              </div>

              {/* EMAIL ADDRESS */}
              <div className="form-group mt-5">
                <label className="text-sm font-ibm-semibold text-black">
                  Email address <span className="text-red-500">*</span>
                  <div className="mt-1">
                    <Input
                      type="text"
                      name="email"
                      id="email"
                      placeholder="Email Address"
                      value={registerHandler.values.email}
                      onChange={registerHandler.handleChange}
                      errorHandler={
                        registerHandler.errors.email &&
                        registerHandler.touched.email
                      }
                    />
                  </div>
                </label>
              </div>
              {/* PASSWORD */}
              <div className="form-group mt-5">
                <label className="text-sm font-ibm-semibold text-black">
                  Password <span className="text-red-500">*</span>
                  <div className="mt-1 relative">
                    <Input
                      type={registerPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      placeholder="Password"
                      value={registerHandler.values.password}
                      onChange={registerHandler.handleChange}
                      errorHandler={
                        registerHandler.errors.password &&
                        registerHandler.touched.password
                      }
                    />
                    <Button
                      className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 !absolute top-2 right-2"
                      onClick={() => setRegisterPassword(!registerPassword)}
                    >
                      {registerPassword ? (
                        <IoMdEyeOff className="w-5 h-5 text-greyText" />
                      ) : (
                        <IoMdEye className="w-5 h-5 text-greyText" />
                      )}
                    </Button>
                  </div>
                </label>
              </div>

              <div className="mt-5">
                <LoadingButton
                  type="submit"
                  label="Create your account"
                  loading={loader}
                />
              </div>
            </form>
            <p className="text-sm font-ibm-semibold text-black mt-3 text-center">
              Already have an account?{" "}
              <a className=" text-[#003087]" href="/">
                Sign in instead
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="py-3 border-t border-authBorder bg-white">
        <div className="container">
          <div className="flex items-center justify-between">
            <p className="text-sm font-ibm-regular text-black">
              Copyright 2025 © Farmers Insurance. All rights reserved.
            </p>
            <p className="text-sm font-ibm-regular text-black">
              Designed by{" "}
              <a
                href="https://www.roundtechsquare.com"
                target="_blank"
                rel="noreferrer"
                className="text-sm font-ibm-semibold text-blue-500"
              >
                RoundTechSquare
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuthLogin;
